<template>
  <div class="bilibili-form-container">
    <el-card class="form-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <el-icon>
            <Database />
          </el-icon>
          <span>B站用户积分管理</span>
        </div>
      </template>

      <el-form ref="userFormRef" :model="userForm" :rules="formRules" label-width="100px" class="user-form">
        <!-- B站UID -->
        <el-form-item label="B站UID" prop="uid">
          <el-input v-model="userForm.uid" placeholder="请输入B站UID" @keyup.enter="submitForm" @input="handleUidInput" />
        </el-form-item>

        <!-- 昵称 -->
        <el-form-item label="昵称" prop="name">
          <el-input v-model="userForm.name" placeholder="请输入用户昵称（选填）" @keyup.enter="submitForm" />
        </el-form-item>

        <!-- 积分 -->
        <el-form-item label="积分" prop="integral">
          <el-input-number v-model="userForm.integral" placeholder="请输入积分" @keyup.enter="submitForm" />
        </el-form-item>

        <!-- 记录 -->
        <el-form-item label="记录" prop="Recording">
          <el-input v-model="userForm.Recording" placeholder="请输入记录（例：2025.6上舰+8;兑换围巾-20）" type="textarea" :rows="4"
            @keyup.enter="submitForm" />
        </el-form-item>

        <!-- 操作按钮 -->
        <el-form-item style="text-align: center; margin-top: 20px;">
          <el-button type="primary" :loading="submitLoading" @click="submitForm">
            提交
          </el-button>
          <el-button @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 批量导入 -->
    <el-card class="form-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <el-icon><Upload /></el-icon>
          <span>批量导入积分（Excel / CSV）</span>
        </div>
      </template>

      <div class="import-bar">
        <el-button :loading="tplLoading" @click="downloadTemplate">⬇ 下载导入模板</el-button>
        <input ref="fileInput" type="file" accept=".xlsx,.xls,.csv" class="hidden-input" @change="onPick" />
        <el-button @click="fileInput?.click()">📄 选择表格</el-button>
        <span class="file-name" v-if="pickedName">{{ pickedName }}</span>
        <el-button type="primary" :disabled="!pickedFile" :loading="previewLoading" @click="doPreview">预览</el-button>
        <el-button type="success" :disabled="!canImport" :loading="importLoading" @click="doImport">确认导入</el-button>
        <el-button v-if="rows.length" @click="clearImport">清空</el-button>
      </div>

      <p class="import-tip">
        · 第一行必须是表头：<b>B站UID / 昵称 / 积分 / 记录</b>（用「下载导入模板」给的那份最省事）<br />
        · 积分是<b>变动值</b>：正数加分、负数扣分；扣成负数的那一行会失败，不会写进去<br />
        · 「记录」会追加到该用户原有记录后面（用 / 分隔）；一次最多 2000 行，导入前先点「预览」看一眼
      </p>

      <el-alert v-if="resultMsg" :title="resultMsg" :type="errCount ? 'warning' : 'success'" :closable="false" show-icon
        style="margin: 10px 0;" />

      <el-table v-if="rows.length" :data="rows" size="small" max-height="360" style="width: 100%">
        <el-table-column prop="line" label="行" width="60" />
        <el-table-column prop="uid" label="B站UID" width="130" />
        <el-table-column prop="name" label="昵称" width="120" />
        <el-table-column prop="integral" label="积分" width="90" />
        <el-table-column prop="recording" label="记录" min-width="150" show-overflow-tooltip />
        <el-table-column label="状态" min-width="220">
          <template #default="s">
            <el-tag :type="s.row.ok ? 'success' : 'danger'" size="small">{{ s.row.ok ? '可以导入' : '有问题' }}</el-tag>
            <span class="row-note">{{ s.row.ok ? '' : s.row.reason }}</span>
            <span class="row-note" v-if="s.row.ok && s.row.after !== undefined && s.row.after !== null">导入后 {{ s.row.after }}</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';
import { Database, Info, Loading, Upload } from '@element-plus/icons-vue';

// 表单引用
const userFormRef = ref(null);

// 表单数据
const userForm = reactive({
  uid: '',
  name: '',
  integral: '',
  Recording: ''
});

// 表单验证规则
const formRules = reactive({
  uid: [
    { required: true, message: '请输入B站UID', trigger: 'blur' },
    { pattern: /^\d+$/, message: 'B站UID只能包含数字', trigger: 'blur' }
  ],
  name: [], // 昵称设为选填
  integral: [
    { required: true, message: '请输入积分', trigger: 'blur' },
    { type: 'number', message: '请输入有效数字', trigger: 'blur' }
  ],
  Recording: [
    { required: true, message: '请输入记录', trigger: 'blur' },
  ]
});

// 提交状态
const submitLoading = ref(false);

// 处理UID只能输入数字
const handleUidInput = (value) => {
  // 只保留数字
  userForm.uid = value.replace(/\D/g, '');
};

// 提交表单
const submitForm = () => {
  userFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        submitLoading.value = true;
        
        // 发送POST请求
        const response = await axios.post('/adminapi/integral/add', {
          uid: userForm.uid,
          name: userForm.name,
          integral: userForm.integral,
          Recording: userForm.Recording
        });
        
        // 处理响应
        if (response.data && response.data.code === 200) {
          if(response.data.message === '积分不能为负数'){
            ElMessage.error('积分不足');
          }
          ElMessage.success('积分记录添加成功');
          resetForm();
        } else {
          ElMessage.error(response.data.message || '提交失败，请重试');
        }
      } catch (error) {
        console.error('提交错误:', error);
        ElMessage.error('网络错误，请检查连接');
      } finally {
        submitLoading.value = false;
      }
    } else {
      ElMessage.error('请检查表单输入');
      return false;
    }
  });
};

// 重置表单
const resetForm = () => {
  userFormRef.value.resetFields();
};

/* ---------------- 批量导入 ---------------- */
const fileInput = ref(null);
const pickedFile = ref(null);
const pickedName = ref('');
const tplLoading = ref(false);
const previewLoading = ref(false);
const importLoading = ref(false);
const rows = ref([]);
const resultMsg = ref('');
const errCount = ref(0);

const canImport = computed(() => rows.value.length > 0 && rows.value.some((r) => r.ok));

const onPick = (e) => {
  const f = (e.target.files || [])[0];
  pickedFile.value = f || null;
  pickedName.value = f ? f.name : '';
  rows.value = [];
  resultMsg.value = '';
  errCount.value = 0;
};

const clearImport = () => {
  pickedFile.value = null;
  pickedName.value = '';
  if (fileInput.value) fileInput.value.value = '';
  rows.value = [];
  resultMsg.value = '';
  errCount.value = 0;
};

/** 下载模板（带 token，所以走 axios 拿 blob，不能直接 a.href） */
const downloadTemplate = async () => {
  tplLoading.value = true;
  try {
    const res = await axios.get('/adminapi/integral/template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = '积分导入模板.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    ElMessage.success('模板已开始下载');
  } catch (e) {
    ElMessage.error('模板下载失败：' + (e.response?.data?.message || e.message));
  } finally {
    tplLoading.value = false;
  }
};

const sendFile = async (dryRun) => {
  const fd = new FormData();
  fd.append('file', pickedFile.value);
  if (dryRun) fd.append('dryRun', '1');
  const res = await axios.post('/adminapi/integral/import', fd, {
    headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0
  });
  return res.data || {};
};

const doPreview = async () => {
  if (!pickedFile.value) return;
  previewLoading.value = true;
  try {
    const d = await sendFile(true);
    if (d.code !== 200) { ElMessage.error(d.message || '预览失败'); return; }
    rows.value = d.rows || [];
    errCount.value = rows.value.filter((r) => !r.ok).length;
    resultMsg.value = d.message || '';
  } catch (e) {
    ElMessage.error('预览失败：' + (e.response?.data?.message || e.message));
  } finally {
    previewLoading.value = false;
  }
};

const doImport = async () => {
  if (!pickedFile.value) return;
  try {
    await ElMessageBox.confirm(
      '确定把这 ' + rows.value.filter((r) => r.ok).length + ' 行积分写进数据库吗？这一步会真的改分。',
      '确认导入', { confirmButtonText: '确认导入', cancelButtonText: '再想想', type: 'warning' }
    );
  } catch (e) { return; }
  importLoading.value = true;
  try {
    const d = await sendFile(false);
    if (d.code !== 200) { ElMessage.error(d.message || '导入失败'); return; }
    rows.value = d.rows || [];
    errCount.value = rows.value.filter((r) => !r.ok).length;
    resultMsg.value = d.message || '';
    if (d.failed) ElMessage.warning(d.message);
    else ElMessage.success(d.message || '导入完成');
  } catch (e) {
    ElMessage.error('导入失败：' + (e.response?.data?.message || e.message));
  } finally {
    importLoading.value = false;
  }
};

onMounted(() => {
  // 初始化焦点到UID输入框
  const uidInput = document.querySelector('input[placeholder="请输入B站UID"]');
  if (uidInput) {
    uidInput.focus();
  }
});
</script>

<style lang="scss" scoped>
.import-bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.hidden-input { display: none; }
.file-name { font-size: 12px; color: #606266; }
.import-tip { margin: 10px 0 0; font-size: 12px; color: #909399; line-height: 1.9; }
.row-note { font-size: 12px; color: #f56c6c; margin-left: 6px; }

.bilibili-form-container {
  padding: 30px;
  max-width: 800px;
  margin: 0 auto;

  .form-card {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    }
  }

  .card-header {
    display: flex;
    align-items: center;
    color: #303133;
    font-size: 18px;
    font-weight: 500;

    .el-icon {
      margin-right: 10px;
      color: #409eff;
    }
  }

  .user-form {
    .el-form-item {
      margin-bottom: 20px;
    }

    .el-input,
    .el-input-number {
      &::v-deep {
        .el-input__inner {
          border-radius: 4px;
          transition: border-color 0.3s, box-shadow 0.3s;
        }

        &:focus-within .el-input__inner {
          border-color: #409eff;
          box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
        }
      }
    }

    .record-hint {
      display: flex;
      align-items: center;
      margin-top: 8px;
      color: #909399;
      font-size: 12px;
    }
  }
}
</style>